import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import JsonLd from '@/components/base/JsonLd';
import { getComicById, comics } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const [isReading, setIsReading] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState<number | null>(null);
  const [readingProgress, setReadingProgress] = useState(0);
  const readerRef = useRef<HTMLDivElement>(null);

  const comic = getComicById(id || '');

  useEffect(() => {
    setIsReading(false);
    setSelectedChapter(null);
    setReadingProgress(0);
  }, [id]);

  useEffect(() => {
    if (!isReading) return;
    const handleScroll = () => {
      if (!readerRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      const totalScroll = scrollHeight - clientHeight;
      if (totalScroll > 0) {
        setReadingProgress(Math.round((scrollTop / totalScroll) * 100));
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isReading]);

  const relatedComics = comic
    ? comics.filter((c) => c.categorySlug === comic.categorySlug && c.id !== comic.id).slice(0, 6)
    : [];

  if (!comic) {
    return (
      <>
        <Header />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <div className="w-24 h-24 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-error-warning-line text-4xl text-foreground-400"></i>
            </div>
            <p className="text-lg text-foreground-600 mb-4">漫画未找到</p>
            <Link
              to="/"
              className="inline-block px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (isReading && selectedChapter) {
    const chapter = comic.chapters.find((ch) => ch.number === selectedChapter);
    const chapterImages = Array.from(
      { length: chapter?.imageCount || 40 },
      (_, i) => i + 1,
    );

    return (
      <>
        <Header />
        <main className="min-h-screen pt-16 pb-20" ref={readerRef}>
          {/* Reading Progress Bar */}
          <div className="fixed top-16 left-0 right-0 h-0.5 bg-background-200/70 z-40">
            <div
              className="h-full bg-primary-500 transition-all duration-300"
              style={{ width: `${readingProgress}%` }}
            ></div>
          </div>

          {/* Chapter Header */}
          <div className="bg-background-50 sticky top-16 z-30 border-b border-background-200/70">
            <div className="max-w-[1400px] mx-auto px-4 py-3 flex items-center justify-between">
              <div>
                <nav className="flex items-center gap-2 text-xs text-foreground-600 mb-0.5">
                  <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line"></i>
                  </span>
                  <Link to={`/comic/${comic.id}`} className="hover:text-primary-500 transition-colors whitespace-nowrap">{comic.title}</Link>
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line"></i>
                  </span>
                  <span className="text-foreground-950 font-medium">第{selectedChapter}话</span>
                </nav>
                <p className="text-sm font-semibold text-foreground-950">
                  {comic.title} - 第{selectedChapter}话
                </p>
              </div>
              <div className="flex items-center gap-2">
                {selectedChapter > 1 && (
                  <button
                    onClick={() => setSelectedChapter(selectedChapter - 1)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-foreground-600 hover:text-foreground-950 bg-background-100 hover:bg-background-200/70 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-left-s-line"></i>
                    </span>
                    上一话
                  </button>
                )}
                {selectedChapter < comic.chapters.length && (
                  <button
                    onClick={() => setSelectedChapter(selectedChapter + 1)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    下一话
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-s-line"></i>
                    </span>
                  </button>
                )}
                <button
                  onClick={() => {
                    setIsReading(false);
                    setSelectedChapter(null);
                  }}
                  className="ml-2 px-3 py-1.5 rounded-lg text-xs font-medium text-foreground-600 hover:text-foreground-950 bg-background-100 hover:bg-background-200/70 transition-colors whitespace-nowrap cursor-pointer"
                >
                  返回详情
                </button>
              </div>
            </div>
          </div>

          {/* Reading Content */}
          <div className="max-w-[780px] mx-auto px-2 py-6">
            <p className="text-center text-xs text-foreground-500 mb-6">
              下拉式完整版免费阅读 - 天天漫画网
            </p>
            <div className="space-y-0">
              {chapterImages.map((imgNum) => (
                <div key={imgNum} className="bg-foreground-100 rounded-lg overflow-hidden mb-0.5">
                  <div className="w-full aspect-[3/4] flex items-center justify-center text-foreground-400">
                    <div className="text-center">
                      <span className="w-16 h-16 flex items-center justify-center mx-auto mb-2">
                        <i className="ri-image-line text-4xl"></i>
                      </span>
                      <p className="text-xs">第{imgNum}页</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center py-8 border-t border-background-200/70 mt-6">
              <p className="text-sm text-foreground-600 mb-4">
                — 第{selectedChapter}话 已读完 —
              </p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                {selectedChapter > 1 && (
                  <button
                    onClick={() => setSelectedChapter(selectedChapter - 1)}
                    className="px-5 py-2 rounded-full text-sm font-medium text-foreground-700 border-2 border-foreground-200 hover:border-foreground-400 hover:text-foreground-950 transition-all whitespace-nowrap cursor-pointer"
                  >
                    上一话
                  </button>
                )}
                <button
                  onClick={() => {
                    setIsReading(false);
                    setSelectedChapter(null);
                  }}
                  className="px-5 py-2 rounded-full text-sm font-medium text-foreground-700 border-2 border-foreground-200 hover:border-foreground-400 hover:text-foreground-950 transition-all whitespace-nowrap cursor-pointer"
                >
                  返回目录
                </button>
                {selectedChapter < comic.chapters.length && (
                  <button
                    onClick={() => setSelectedChapter(selectedChapter + 1)}
                    className="px-5 py-2 rounded-full text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    下一话
                  </button>
                )}
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: `${comic.title} - 天天漫画网`,
          description: comic.description,
          author: { '@type': 'Person', name: comic.author },
          datePublished: comic.chapters[0]?.createdAt,
          dateModified: comic.updatedAt,
          image: comic.cover,
          publisher: { '@type': 'Organization', name: '天天漫画网' },
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.tiantianmanhua.com' },
            { '@type': 'ListItem', position: 2, name: comic.category, item: `https://www.tiantianmanhua.com/category/${comic.categorySlug}` },
            { '@type': 'ListItem', position: 3, name: comic.title },
          ],
        }}
      />
      <Header />
      <main className="min-h-screen pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-3">
            <nav className="flex items-center gap-2 text-xs text-foreground-600">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <Link to={`/category/${comic.categorySlug}`} className="hover:text-primary-500 transition-colors whitespace-nowrap">{comic.category}</Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <span className="text-foreground-950 font-medium">{comic.title}</span>
            </nav>
          </div>
        </div>

        {/* Comic Info */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-8 md:py-10">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="flex-shrink-0">
              <div className="w-[200px] h-[280px] md:w-[240px] md:h-[340px] rounded-xl overflow-hidden bg-background-100">
                <img
                  src={comic.cover}
                  alt={comic.title}
                  title={`${comic.title} - 天天漫画网下拉式完整版免费阅读`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">{comic.title}</h1>
              <div className="flex items-center gap-4 mt-3 flex-wrap">
                <span className="text-sm text-foreground-600">作者：{comic.author}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300 flex-shrink-0"></span>
                <span className="text-sm text-foreground-600">分类：{comic.category}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300 flex-shrink-0"></span>
                <span className={`px-2.5 py-0.5 rounded-md text-xs font-semibold whitespace-nowrap ${
                  comic.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {comic.status}
                </span>
              </div>
              <div className="flex items-center gap-4 mt-3 flex-wrap">
                <div className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-sm text-accent-500"></i>
                  </span>
                  <span className="text-sm font-semibold text-accent-500">{comic.rating}</span>
                </div>
                <div className="flex items-center gap-1 text-sm text-foreground-600">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line"></i>
                  </span>
                  {(comic.views / 10000).toFixed(1)}万次阅读
                </div>
                <div className="flex items-center gap-1 text-sm text-foreground-600">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-heart-line"></i>
                  </span>
                  {(comic.favorites / 10000).toFixed(1)}万收藏
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-background-100 text-foreground-600 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <p className="text-sm text-foreground-600 mt-4 leading-relaxed max-w-2xl">
                {comic.description}
              </p>

              <div className="flex items-center gap-3 mt-6 flex-wrap">
                {comic.chapters.length > 0 && (
                  <button
                    onClick={() => setSelectedChapter(1)}
                    className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    从第1话开始阅读
                  </button>
                )}
                <button
                  onClick={() => {
                    const lastChapter = comic.chapters[comic.chapters.length - 1];
                    if (lastChapter) setSelectedChapter(lastChapter.number);
                  }}
                  className="px-8 py-3 rounded-full text-sm font-medium text-foreground-700 border-2 border-foreground-200 hover:border-foreground-400 hover:text-foreground-950 transition-all whitespace-nowrap cursor-pointer"
                >
                  阅读最新章节
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Chapter List */}
        <section className="bg-background-100 py-10">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-foreground-950">章节列表</h2>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-600">
                  共 {comic.chapters.length} 话
                </span>
                <button
                  onClick={() => {
                    const newOrder = [...comic.chapters].reverse();
                    comic.chapters.length = 0;
                    comic.chapters.push(...newOrder);
                  }}
                  className="text-xs text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
                >
                  倒序
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {comic.chapters.map((ch) => (
                <button
                  key={ch.id}
                  onClick={() => setSelectedChapter(ch.number)}
                  className="text-left px-4 py-3 rounded-lg bg-background-50 hover:bg-background-200/70 transition-colors cursor-pointer"
                >
                  <p className="text-sm font-medium text-foreground-950 truncate">
                    第{ch.number}话
                  </p>
                  <p className="text-[11px] text-foreground-500 mt-0.5">{ch.createdAt}</p>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-10">
            <div className="mb-6">
              <h2 className="text-lg font-bold text-foreground-950">相关推荐</h2>
              <p className="text-sm text-foreground-600 mt-1">更多{comic.category}类漫画</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
              {relatedComics.map((c) => (
                <ComicCard key={c.id} comic={c} size="small" />
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}